// CR051: Flyer Capture Console - 4-Tab Layout with Left Rail
import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { MerchantList } from '@/components/admin/capture-console/MerchantList';
import { CaptureSetupTab } from '@/components/admin/capture-console/CaptureSetupTab';
import { RecipeBuilderTab } from '@/components/admin/capture-console/RecipeBuilderTab';
import { RunsOutputsTab } from '@/components/admin/capture-console/RunsOutputsTab';
import { TemplatesTab } from '@/components/admin/capture-console/TemplatesTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { Settings, Wrench, Play, FileStack } from 'lucide-react';

export interface MerchantBinding {
  id: string;
  merchant_id: string;
  merchant_name: string;
  start_url: string;
  postal_code: string;
  recipe_id: string | null;
  run_strategy: 'STRICT_RECIPE' | 'RECIPE_WITH_FALLBACK';
  enabled: boolean;
  merchant_overrides: Record<string, unknown>;
  last_run_id: string | null;
  last_run_status: string | null;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface RecipeTemplate {
  id: string;
  recipe_name: string;
  description: string | null;
  viewer_type: string | null;
  default_start_url: string | null;
  steps: unknown[];
  recipe_defaults: Record<string, unknown>;
  success_criteria: Record<string, unknown>;
  is_system_template: boolean;
  created_at: string;
  updated_at: string;
}

const FlyerCaptureConsole = () => {
  const { isAdmin } = useAuth();
  const [merchants, setMerchants] = useState<MerchantBinding[]>([]);
  const [recipes, setRecipes] = useState<RecipeTemplate[]>([]);
  const [selectedMerchant, setSelectedMerchant] = useState<MerchantBinding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('setup');

  useEffect(() => {
    if (isAdmin) loadData();
  }, [isAdmin]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [merchantsRes, recipesRes] = await Promise.all([
        supabase.from('capture_merchant_bindings').select('*').order('merchant_name'),
        supabase.from('capture_recipe_templates').select('*').order('recipe_name')
      ]);
      if (merchantsRes.error) throw merchantsRes.error;
      if (recipesRes.error) throw recipesRes.error;

      const merchantData = (merchantsRes.data || []) as unknown as MerchantBinding[];
      const recipeData = (recipesRes.data || []) as unknown as RecipeTemplate[];
      
      setMerchants(merchantData);
      setRecipes(recipeData);
      
      if (!selectedMerchant && merchantData.length > 0) {
        setSelectedMerchant(merchantData[0]);
      }
    } catch (err) {
      console.error('[CR051] Load error:', err);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMerchantUpdate = async (updated: MerchantBinding) => {
    try {
      const { error } = await supabase
        .from('capture_merchant_bindings')
        .update({
          start_url: updated.start_url,
          postal_code: updated.postal_code,
          recipe_id: updated.recipe_id,
          run_strategy: updated.run_strategy,
          enabled: updated.enabled,
          merchant_overrides: updated.merchant_overrides
        } as never)
        .eq('id', updated.id);

      if (error) throw error;
      
      setMerchants(prev => prev.map(m => m.id === updated.id ? updated : m));
      setSelectedMerchant(updated);
      toast.success('Configuration saved');
    } catch (err) {
      console.error('[CR051] Update error:', err);
      toast.error('Failed to save');
    }
  };

  const handleRecipeUpdate = async (updated: RecipeTemplate) => {
    try {
      const { error } = await supabase
        .from('capture_recipe_templates')
        .update({
          recipe_name: updated.recipe_name,
          description: updated.description,
          viewer_type: updated.viewer_type,
          steps: updated.steps,
          recipe_defaults: updated.recipe_defaults,
          success_criteria: updated.success_criteria
        } as never)
        .eq('id', updated.id);

      if (error) throw error;
      
      setRecipes(prev => prev.map(r => r.id === updated.id ? updated : r));
      toast.success('Recipe updated');
    } catch (err) {
      console.error('[CR051] Recipe update error:', err);
      toast.error('Failed to update recipe');
    }
  };

  const handleCreateFromTemplate = async (template: RecipeTemplate) => {
    if (!selectedMerchant) {
      toast.error('Select a merchant first');
      return;
    }

    // Check if merchant already has a recipe
    const existingRecipe = recipes.find(r => 
      !r.is_system_template && 
      r.recipe_name.toLowerCase().includes(selectedMerchant.merchant_name.toLowerCase())
    );
    if (existingRecipe) {
      toast.error(`${selectedMerchant.merchant_name} already has a recipe: ${existingRecipe.recipe_name}`);
      return;
    }

    try {
      // Create a new recipe based on the template
      const newRecipeName = `${selectedMerchant.merchant_name} - ${template.viewer_type || 'Custom'}`;
      
      const { data: newRecipe, error: createError } = await supabase
        .from('capture_recipe_templates')
        .insert([{
          recipe_name: newRecipeName,
          description: `Recipe for ${selectedMerchant.merchant_name} based on ${template.recipe_name}`,
          viewer_type: template.viewer_type,
          default_start_url: selectedMerchant.start_url,
          steps: template.steps as never,
          recipe_defaults: template.recipe_defaults as never,
          success_criteria: template.success_criteria as never,
          is_system_template: false
        }])
        .select()
        .single();

      if (createError) throw createError;

      // Update the merchant binding to use this new recipe
      const { error: bindError } = await supabase
        .from('capture_merchant_bindings')
        .update({ recipe_id: newRecipe.id } as never)
        .eq('id', selectedMerchant.id);

      if (bindError) throw bindError;

      // Update local state
      const typedRecipe = newRecipe as unknown as RecipeTemplate;
      setRecipes(prev => [...prev, typedRecipe]);
      setMerchants(prev => prev.map(m => 
        m.id === selectedMerchant.id ? { ...m, recipe_id: newRecipe.id } : m
      ));
      setSelectedMerchant(prev => prev ? { ...prev, recipe_id: newRecipe.id } : null);

      toast.success(`Created recipe "${newRecipeName}" and assigned to ${selectedMerchant.merchant_name}`);
      setActiveTab('recipe'); // Switch to Recipe Builder tab
    } catch (err) {
      console.error('[CR051] Create from template error:', err);
      toast.error('Failed to create recipe from template');
    }
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Admin access required</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-4rem)]">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Left Rail: Merchant List */}
          <ResizablePanel defaultSize={22} minSize={18} maxSize={30}>
            <MerchantList
              merchants={merchants}
              selectedMerchant={selectedMerchant}
              onSelectMerchant={setSelectedMerchant}
              isLoading={isLoading}
              onRefresh={loadData}
            />
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Main Area: 4 Tabs */}
          <ResizablePanel defaultSize={78}>
            <div className="h-full flex flex-col p-4">
              <div className="flex items-center justify-between mb-4">
                <h1 className="text-2xl font-bold">
                  {selectedMerchant?.merchant_name || 'Flyer Capture Console'}
                </h1>
              </div>

              <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="setup" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Capture Setup
                  </TabsTrigger>
                  <TabsTrigger value="recipe" className="flex items-center gap-2">
                    <Wrench className="h-4 w-4" />
                    Recipe Builder
                  </TabsTrigger>
                  <TabsTrigger value="runs" className="flex items-center gap-2">
                    <Play className="h-4 w-4" />
                    Runs & Outputs
                  </TabsTrigger>
                  <TabsTrigger value="templates" className="flex items-center gap-2">
                    <FileStack className="h-4 w-4" />
                    Templates
                  </TabsTrigger>
                </TabsList>

                <div className="flex-1 overflow-auto mt-4">
                  <TabsContent value="setup" className="h-full m-0">
                    {selectedMerchant ? (
                      <CaptureSetupTab
                        merchant={selectedMerchant}
                        recipes={recipes}
                        onUpdate={handleMerchantUpdate}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-64 text-muted-foreground">
                        Select a merchant from the left panel
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="recipe" className="h-full m-0">
                    {selectedMerchant?.recipe_id ? (
                      <RecipeBuilderTab
                        recipeId={selectedMerchant.recipe_id}
                        recipes={recipes}
                        merchantOverrides={selectedMerchant.merchant_overrides}
                        onRecipeUpdate={handleRecipeUpdate}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-64 text-muted-foreground">
                        {selectedMerchant ? 'No recipe assigned' : 'Select a merchant first'}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="runs" className="h-full m-0">
                    {selectedMerchant ? (
                      <RunsOutputsTab
                        merchantId={selectedMerchant.merchant_id}
                        merchantName={selectedMerchant.merchant_name}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-64 text-muted-foreground">
                        Select a merchant first
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="templates" className="h-full m-0">
                    <TemplatesTab
                      templates={recipes.filter(r => r.is_system_template)}
                      onCreateFromTemplate={handleCreateFromTemplate}
                    />
                  </TabsContent>
                </div>
              </Tabs>
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </AdminLayout>
  );
};

export default FlyerCaptureConsole;
